"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Avatar, Paper, useTheme } from "@mui/material";
import { GoogleMap, Marker, Polyline, OverlayView } from "@react-google-maps/api";
import StaffOverlay from "./StaffOverlay";
import { useGetAgentLocationHistoryQuery } from "../../../redux/api/agentLocationApi";
import { dayRange, STATUS_COLORS, todayStr, toHistoryPath } from "../utils";

const containerStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "16px",
};

// Fit map bounds to a path
const mapStyles = {
  standard: [],
  light: [
    { featureType: "all", elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
    { featureType: "all", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  ],
  dark: [
    { elementType: "geometry", stylers: [{ color: "#1b2430" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#1b2430" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#8a9bb5" }] },
    { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#33415c" }] },
    { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#b9c5d6" }] },
    { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#8a9bb5" }] },
    { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#1f3326" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#2c3a4d" }] },
    { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#1b2430" }] },
    { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#8a9bb5" }] },
    { featureType: "transit", elementType: "geometry", stylers: [{ color: "#33415c" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#14202e" }] },
    { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#5f7d99" }] },
  ],
};


const DEFAULT_CENTER = { lat: 25.2048, lng: 55.2708 };

const fitBoundsToPath = (map, path) => {
  if (!map || !path?.length || !window.google) return;
  const bounds = new window.google.maps.LatLngBounds();
  path.forEach((p) => bounds.extend(p));
  map.fitBounds(bounds, 100);
};

const MapView = ({ selectedStaff, staffList = [] }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [showHistory, setShowHistory] = useState(false);
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [playing, setPlaying] = useState(false);
  const [playIndex, setPlayIndex] = useState(0);
  const [avatarPosition, setAvatarPosition] = useState(null);
  const [hoveredStaff, setHoveredStaff] = useState(null);
  const [mapStyle, setMapStyle] = useState("standard");

  const mapRef = useRef(null);
  const playIntervalRef = useRef(null);
  const initializedRef = useRef(false);
  const allAgentsInitializedRef = useRef(false);

  const [mapReady, setMapReady] = useState(false);
  const onMapLoad = (map) => {
    mapRef.current = map;
    // GoogleMap has no default view; without one it renders nothing until something sets it.
    map.setCenter(DEFAULT_CENTER);
    map.setZoom(10);
    setMapReady(true);
  };

  const isSingle = selectedStaff && selectedStaff.id !== "all";
  const historyArgs = isSingle ? { agentId: selectedStaff.id, ...dayRange(selectedDate) } : null;
  const {
    data: historyData,
    isFetching: historyLoading,
    isError: historyError,
  } = useGetAgentLocationHistoryQuery(historyArgs, { skip: !showHistory || !historyArgs });
  const historyPath = useMemo(
    () => (showHistory ? toHistoryPath(historyData?.trail) : []),
    [showHistory, historyData]
  );
  const displayPath = historyPath.map((p) => ({ lat: p.lat, lng: p.lng }));

  const allOptions = useMemo(
    () => ({
      styles: isDark ? mapStyles.dark : [],
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: true,
      fullscreenControl: true,
    }),
    [isDark]
  );
  const singleOptions = useMemo(
    () => ({
      styles: isDark ? mapStyles.dark : mapStyles[mapStyle],
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: true,
      scaleControl: true,
      streetViewControl: true,
      rotateControl: true,
      fullscreenControl: true,
    }),
    [isDark, mapStyle]
  );

  // Reset when staff changes
  useEffect(() => {
    if (!selectedStaff) {
      setShowHistory(false);
      setPlayIndex(0);
      setPlaying(false);
      initializedRef.current = false;
      setAvatarPosition(null);
      return;
    }

    initializedRef.current = false;
    setPlayIndex(0);

    // Initialize avatar position to liveLocation
    if (selectedStaff.liveLocation) setAvatarPosition(selectedStaff.liveLocation);
  }, [selectedStaff?.id]);

  // Update avatarPosition on playIndex change for history
  useEffect(() => {
    if (showHistory && historyPath.length > 0 && playIndex < historyPath.length) {
      setAvatarPosition(historyPath[playIndex]);
    }
  }, [playIndex, showHistory, historyPath]);

  // Follow live position when not viewing history
  useEffect(() => {
    if (!showHistory && selectedStaff?.liveLocation) setAvatarPosition(selectedStaff.liveLocation);
  }, [showHistory, selectedStaff?.liveLocation]);

  // History playback
  useEffect(() => {
    if (!playing) {
      clearInterval(playIntervalRef.current);
      return;
    }

    const path = historyPath;
    if (path.length === 0) {
      setPlaying(false);
      return;
    }

    playIntervalRef.current = setInterval(() => {
      setPlayIndex((prev) => {
        const next = prev + 1;
        if (next >= path.length) {
          clearInterval(playIntervalRef.current);
          setPlaying(false);
          return path.length - 1;
        }
        return next;
      });
    }, 700);

    return () => clearInterval(playIntervalRef.current);
  }, [playing, historyPath]);

  // Initial map center / zoom
  useEffect(() => {
    if (!mapRef.current || !selectedStaff) return;
    if (selectedStaff.id === "all") return;

    const path = historyPath;

    if (showHistory) {
      if (path.length > 0) {
        mapRef.current.setCenter(path[0]);
        mapRef.current.setZoom(15);
        initializedRef.current = true;
      }
    } else if (!initializedRef.current && selectedStaff.liveLocation) {
      mapRef.current.panTo(selectedStaff.liveLocation);
      mapRef.current.setZoom(14);
      initializedRef.current = true;
    }
  }, [showHistory, historyPath, selectedStaff?.id, mapReady]);

  // Fit bounds for all agents
  useEffect(() => {
    if (selectedStaff?.id !== "all" || !mapRef.current || !window.google) return;
    if (allAgentsInitializedRef.current) return;

    const located = staffList.filter((s) => s.liveLocation);
    if (!located.length) return;

    const bounds = new window.google.maps.LatLngBounds();
    located.forEach((s) => bounds.extend(s.liveLocation));
    mapRef.current.fitBounds(bounds, 150);

    allAgentsInitializedRef.current = true;
  }, [selectedStaff?.id, staffList, mapReady]);

  useEffect(() => {
    if (selectedStaff?.id !== "all") allAgentsInitializedRef.current = false;
  }, [selectedStaff?.id]);

// ALL AGENTS VIEW
if (selectedStaff?.id === "all") {
  const locatedStaff = staffList.filter((s) => s.liveLocation);

  return (
    <Box
      sx={{
        flexGrow: 1,
        flexBasis: 0,
        minWidth: 0,
        width: { xs: "100%", md: "auto" },
        minHeight: 400,
        borderRadius: "16px",
        overflow: "hidden",
        height: "80vh",
        position: "relative",
        boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
        background: "linear-gradient(145deg, #0d1117, #161b22)",
      }}
    >
      <GoogleMap
        mapContainerStyle={containerStyle}
        onLoad={onMapLoad}
        options={allOptions}
      >
        {locatedStaff.map((s) => {
          return (
            <OverlayView
              key={s.id}
              position={s.liveLocation}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                onMouseEnter={() => setHoveredStaff(s)}
                onMouseLeave={() => setHoveredStaff(null)}
                style={{ cursor: "pointer", transform: "translate(-50%, -50%)" }}
              >
                <Avatar
                  src={s.agentImg || "/static/images/icon-map.png"}
                  alt={s.name}
                  sx={{ width: 50, height: 50, border: `3px solid ${STATUS_COLORS[s.status]}`, transition: "none", opacity: s.status === "Offline" ? 0.6 : 1 }}
                />
              </div>
            </OverlayView>
          );
        })}
      </GoogleMap>

      {hoveredStaff && (
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            bottom: 10,
            left: "50%",
            transform: "translateX(-50%)",
            borderRadius: 2,
            overflow: "hidden",
            width: 400,
            zIndex: 999,
          }}
        >
          <StaffOverlay
            selectedStaff={hoveredStaff}
            showHistory={showHistory}
            setShowHistory={setShowHistory}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            playIndex={playIndex}
            setPlayIndex={setPlayIndex}
            playing={playing}
            setPlaying={setPlaying}
            historyPath={historyPath}
          />
        </Paper>
      )}
    </Box>
  );
}


  // SINGLE STAFF VIEW
  return (
    <Box
      sx={{
        flexGrow: 1,
        flexBasis: 0,
        minWidth: 0,
        width: { xs: "100%", md: "auto" },
        minHeight: 400,
        borderRadius: "16px",
        overflow: "hidden",
        height: "75vh",
        position: "relative",
        boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
        background: "linear-gradient(145deg, #0d1117, #161b22)",
      }}
    >
      <GoogleMap
        mapContainerStyle={containerStyle}
        onLoad={onMapLoad}
        options={singleOptions}
      >
        {/* HISTORY PATH */}
        {showHistory && displayPath.length > 0 && (
          <>
            <Polyline
              path={displayPath}
              options={{ strokeColor: "#56C662", strokeOpacity: 0.8, strokeWeight: 4 }}
            />
            <Marker position={displayPath[0]} label="START" />
            <Marker position={displayPath[displayPath.length - 1]} label="END" />

            {avatarPosition && (
              <OverlayView position={avatarPosition} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <Paper
                    elevation={3}
                    sx={{
                      p: "2px 6px",
                      mb: 0.5,
                      bgcolor: "rgba(0,0,0,0.7)",
                      color: "#fff",
                      borderRadius: 1,
                      fontSize: "0.75rem",
                    }}
                  >
                    {avatarPosition.time
                      ? (() => {
                          const [hour, minute] = avatarPosition.time.split(":").map(Number);
                          const ampm = hour >= 12 ? "PM" : "AM";
                          const hr12 = hour % 12 === 0 ? 12 : hour % 12;
                          return `${hr12}:${minute.toString().padStart(2, "0")} ${ampm}`;
                        })()
                      : ""}
                  </Paper>

                  <Avatar
                    src={selectedStaff.agentImg || "/static/images/icon-map.png"}
                    alt={selectedStaff.name}
                    sx={{
                      width: 60,
                      height: 60,
                      border: "4px solid #56C662",
                    }}
                  />
                </Box>
              </OverlayView>
            )}
          </>
        )}

        {/* LIVE AVATAR */}
        {!showHistory && selectedStaff?.liveLocation && (
          <OverlayView
            position={selectedStaff.liveLocation}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <Avatar
              src={selectedStaff.agentImg || "/static/images/icon-map.png"}
              alt={selectedStaff.name}
              sx={{
                width: 60,
                height: 60,
                border: `4px solid ${STATUS_COLORS[selectedStaff.status]}`,
                transform: "translate(-50%, -50%)",
                transition: "none",
              }}
            />
          </OverlayView>
        )}
      </GoogleMap>

      <StaffOverlay
        selectedStaff={selectedStaff}
        showHistory={showHistory}
        setShowHistory={setShowHistory}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        playIndex={playIndex}
        setPlayIndex={setPlayIndex}
        playing={playing}
        setPlaying={setPlaying}
        historyPath={historyPath}
        historyLoading={historyLoading}
        historyError={historyError}
      />
    </Box>
  );
};

export default MapView;
